import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { aziendeApi } from '../services/api';

/**
 * AziendeManager - Barra con tab per selezionare/gestire le aziende.
 * 
 * Props:
 *  - onSelect(azienda): callback quando l'azienda selezionata cambia
 *  - selectedId: id dell'azienda attualmente selezionata (controlled)
 */
const AziendeManager = ({ onSelect, selectedId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [aziende, setAziende] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabIdx, setTabIdx] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const loadAziende = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await aziendeApi.getAll();
      const list = resp?.data?.data || [];
      setAziende(list);

      // Se c'è un selectedId, seleziona il tab corretto
      if (selectedId) {
        const idx = list.findIndex(a => a.id === selectedId);
        if (idx >= 0) setTabIdx(idx);
      } else {
        // Seleziona la default
        const defIdx = list.findIndex(a => a.is_default);
        const idx = defIdx >= 0 ? defIdx : 0;
        setTabIdx(idx);
        if (list[idx] && onSelect) onSelect(list[idx]);
      }
    } catch (e) {
      console.error('Errore caricamento aziende:', e);
      // Fallback: nessuna azienda, il Settings funzionerà con company-settings
    } finally {
      setLoading(false);
    }
  }, [selectedId, onSelect]);

  useEffect(() => {
    loadAziende();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (event, newIdx) => {
    setTabIdx(newIdx);
    if (aziende[newIdx] && onSelect) {
      onSelect(aziende[newIdx]);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      setCreating(true);
      setError('');
      await aziendeApi.create({ company_name: newName.trim() });
      setDialogOpen(false);
      setNewName('');
      await loadAziende();
    } catch (e) {
      setError(e?.response?.data?.message || 'Errore nella creazione');
    } finally {
      setCreating(false);
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await aziendeApi.setDefault(id);
      await loadAziende();
    } catch (e) {
      console.error('Errore impostazione default:', e);
    }
  };

  const handleDelete = async (id) => {
    try {
      await aziendeApi.delete(id);
      setDeleteConfirm(null);
      await loadAziende();
    } catch (e) {
      setError(e?.response?.data?.message || 'Impossibile eliminare');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">Caricamento aziende...</Typography>
      </Box>
    );
  }

  // Se non ci sono aziende, non mostrare nulla (retrocompat con vecchia modalità)
  if (aziende.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <BusinessIcon color="primary" fontSize="small" />
        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
          Aziende
        </Typography>
        <Tooltip title="Aggiungi nuova azienda">
          <IconButton size="small" color="primary" onClick={() => setDialogOpen(true)}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabIdx}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 40 }}
        >
          {aziende.map((az, idx) => (
            <Tab
              key={az.id}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {az.company_name || `Azienda ${az.id}`}
                  {az.is_default && (
                    <Chip label="Default" size="small" color="primary" variant="outlined"
                      sx={{ height: 18, fontSize: '0.7rem', ml: 0.5 }} />
                  )}
                </Box>
              }
              sx={{ minHeight: 40, py: 0.5, textTransform: 'none' }}
            />
          ))}
        </Tabs>
      </Box>

      {/* Barra azioni per l'azienda selezionata */}
      {aziende[tabIdx] && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, px: 1 }}>
          {!aziende[tabIdx].is_default && (
            <Tooltip title="Imposta come azienda predefinita">
              <IconButton size="small" onClick={() => handleSetDefault(aziende[tabIdx].id)}>
                <StarBorderIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {aziende[tabIdx].is_default && (
            <Tooltip title="Azienda predefinita">
              <StarIcon fontSize="small" color="warning" />
            </Tooltip>
          )}
          {!aziende[tabIdx].is_default && aziende.length > 1 && (
            <Tooltip title="Elimina azienda">
              <IconButton size="small" color="error" onClick={() => setDeleteConfirm(aziende[tabIdx])}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Dialog nuova azienda */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle>Nuova Azienda</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome Azienda"
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annulla</Button>
          <Button onClick={handleCreate} variant="contained" disabled={creating || !newName.trim()}>
            {creating ? <CircularProgress size={20} /> : 'Crea'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog conferma eliminazione */}
        <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullScreen={isMobile}>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent>
          <Typography>
            Eliminare l'azienda <strong>{deleteConfirm?.company_name}</strong>?
            Questa operazione non è reversibile.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Annulla</Button>
          <Button onClick={() => handleDelete(deleteConfirm?.id)} color="error" variant="contained">
            Elimina
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AziendeManager;
